// External Imports
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react'
import Autosuggest from 'react-autosuggest'
import { MdSearch } from '@react-icons/all-files/md/MdSearch'
import { useDispatch, useSelector } from 'react-redux'
import { Link, useNavigate, useLocation, useSearchParams } from 'react-router-dom'
// Internal Imports
import { LanguageContext } from 'context/LanguageContext'
import { ScreenContext } from 'context/ScreenContext'
import { trackProductSearch } from 'middleware/helper'
import {
    searchProducts,
    loadProducts,
    getSearchedProduct,
    setSearchedValue,
    resetSearchedProducts,
} from 'modules/products/actions'
import { util } from 'utils'
import { alcoholUtility } from 'utils/alcoholutils'
import { debounce, isEmpty, shouldShowPickupOnlyMsg } from 'utils/appUtils'
import { getCDNURL } from 'network/api/networkSettings'
import PickupMsg from 'components/common/pickupMsg'
import CustomAddToCartButton from 'components/common/customAddToCartButton'
import searchIcon from '../../assets/images/search.svg'

const effectiveDebounce = new debounce()
const SearchBar = ({panelOpen}) => {
    const productFallbackImage = getCDNURL() + 'assets/images/productFallbackImage.png'

    const { strings, language, frontDefaultLanguage } = useContext(LanguageContext)
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const location = useLocation()
    const [searchParams] = useSearchParams()
    const { mobile } = useContext(ScreenContext)
    const store = useSelector(state => state)

    const getTabFromContext = () => {
        // First priority: tab from URL (single source of truth)
        const urlTab = searchParams.get('tab')
        if (urlTab === 'catering' || urlTab === 'shop') {
            return urlTab
        }
        // Second priority: determine from current route
        const currentPath = location.pathname
        if (currentPath.startsWith('/catering')) {
            return 'catering'
        }
        // Default to shop for all other routes
        return 'shop'
    }
    const { isSignedIn } = util.getJWTDetails()
    const [searchTerm, setSearchTerm] = useState('')
    const [suggestions, setSuggestions] = useState([])
    const [headingTitle, setHeadingTitle] = useState('')
    const [stopBodyScroll, setStopBodyScroll] = useState(false)
    const homePageRes = useSelector(state => state.products.homePageContent)
    const selectedAddressInStore = useSelector(state => state.location.getIn(['selectedAddress']))
    const selectedAddress = selectedAddressInStore?.toJS() || null
    const cateringData = useSelector(state => state.catering)
    const cateringAddress = cateringData?.address || null
    const searchKeysFromConfig = useSelector(state => state.configuration.get('search_terms'))
    const searchedValue = useSelector(state => state.products['searchedValue'])
    const popularSearchKeys = searchKeysFromConfig ? searchKeysFromConfig.toJS() : []
    const resultFromSearchQuery = useSelector(state => state.products.productSearchResults.Items)
    const searchApiInProgress = useSelector(state => state.products.searchingProducts)
    const searchBarRef = useRef(null)
    const otherRef = useRef(null)
    const isLoading = useSelector(state => state.languageChange.get('isLoading'))
    const handleTouch = () => {
        otherRef.current.autowhatever.itemsContainer.style.visibility = ''
        mobile && setStopBodyScroll(true)
        emptySearchDispatch()
    }
    useEffect(() => {
        let element = document.querySelector('.app-container ')
        mobile && stopBodyScroll
            ? (element.style.overflowY = 'hidden')
            : (element.style.overflowY = '')
    }, [stopBodyScroll])
    const handlePopularSearch = e => {
        let key = e.target.value
        const currentTab = getTabFromContext()
        dispatch(setSearchedValue(key))
        const searchQuery = util.searchSuggestRequest(key, false, currentTab)
        dispatch(loadProducts(searchQuery))
        // Need to refactor below line after library supports our complexity
        otherRef.current.autowhatever.itemsContainer.style.visibility = 'hidden'
        navigate({
            pathname: '/search-results',
            search: `?query=${encodeURIComponent(key)}&tab=${currentTab}`,
        })
        trackProductSearch(
            'suggested search',
            e.target.value,
            suggestions.length,
            window.location.pathname,
            store
        )
    }
    useEffect(() => {
        searchBarRef.current.blur()
        document.body.focus()
    }, [location, searchedValue])
    useEffect(() => {
        const currentTab = getTabFromContext()
        const isCateringMode = currentTab === 'catering'
        
        // Always show search results if available
        if (resultFromSearchQuery && !isEmpty(resultFromSearchQuery)) {
            setSuggestions(resultFromSearchQuery)
            setHeadingTitle(strings.searchResultsLabel)
            return
        }
        
        // In catering mode, don't show any fallback suggestions
        if (isCateringMode) {
            setSuggestions([])
            setHeadingTitle('')
            return
        }
        
        // Shop mode - show fallback suggestions
        if (
            isSignedIn &&
            homePageRes?.purchases &&
            !isEmpty(homePageRes.purchases.content) &&
            (!searchApiInProgress || !!!searchTerm.length)
        ) {
            let signedInSearchResults = homePageRes?.purchases?.content || []
            setSuggestions(signedInSearchResults)
            setHeadingTitle(homePageRes?.purchases?.display_name)
        } else if (
            (!searchApiInProgress || !!!searchTerm.length) &&
            isSignedIn &&
            homePageRes?.purchases &&
            isEmpty(homePageRes.purchases)
        ) {
            let commonSearchResults = homePageRes?.customCategoryProducts[0]?.content || []
            setSuggestions(commonSearchResults)
            setHeadingTitle(homePageRes?.customCategoryProducts[0]?.display_name)
        } else if (
            homePageRes?.customCategoryProducts &&
            !isEmpty(homePageRes.customCategoryProducts) &&
            (!searchApiInProgress || !!!searchTerm.length)
        ) {
            let commonSearchResults = homePageRes?.customCategoryProducts[0]?.content || []
            setSuggestions(commonSearchResults)
            setHeadingTitle(homePageRes?.customCategoryProducts[0]?.display_name)
        } else if (!isSignedIn && (searchTerm.length === 0 || resultFromSearchQuery.length === 0)) {
            setSuggestions([])
        }
    }, [homePageRes, resultFromSearchQuery, searchTerm, location.pathname, searchParams])
    const renderPopularItems = () => {
        return (
            popularSearchKeys &&
            popularSearchKeys.default_value &&
            popularSearchKeys.default_value.map((each, index) => (
                <button key={index} value={each} onClick={handlePopularSearch}>
                    {each}
                </button>
            ))
        )
    }
    const renderLocalisedPopularItems = () => {
        return popularSearchKeys.localizations_ui.default_value.map((each, index) => (
            <button
                key={index}
                value={popularSearchKeys.default_value[index]}
                onClick={handlePopularSearch}
            >
                {each}
            </button>
        ))
    }
    const renderSuggestionsContainer = props => {
        const { containerProps, children, query } = props
        return (
            <div {...containerProps}>
                <div className={`searchbar__left ${mobile ? 'fullWidth' : null}`}>
                    {mobile && popularSearchKeys.length ? (
                        <div className={'searchbar__mobileTop'}>
                            <p>{strings.PopularItemsList}</p>
                            <div>
                                {language === frontDefaultLanguage
                                    ? renderPopularItems()
                                    : popularSearchKeys &&
                                        popularSearchKeys.localizations_ui &&
                                        popularSearchKeys.localizations_ui.default_value
                                      ? renderLocalisedPopularItems()
                                      : renderPopularItems()}
                            </div>
                        </div>
                    ) : null}
                    <>
                        {headingTitle !== '' ? <p>{headingTitle}</p> : null}
                        {children}
                    </>
                </div>
                {language === frontDefaultLanguage && !mobile && popularSearchKeys ? (
                    <div className={'searchbar__right'}>
                        <p>{strings.PopularItemsList}</p>
                        {renderPopularItems()}
                    </div>
                ) : !mobile && popularSearchKeys ? (
                    <div className={'searchbar__right'}>
                        <p>{strings.PopularItemsList}</p>
                        {popularSearchKeys &&
                        popularSearchKeys.localizations_ui &&
                        popularSearchKeys.localizations_ui.default_value
                            ? renderLocalisedPopularItems()
                            : renderPopularItems()}
                    </div>
                ) : null}
            </div>
        )
    }
    const handleRowClick = (suggestion, event) => {
        if (!searchTerm.length) emptySearchDispatch()
        // emptySearchDispatch()
        dispatch(getSearchedProduct(suggestion.product_id))
        trackProductSearch(
            'autofill',
            suggestion.name,
            suggestions.length,
            window.location.pathname,
            store
        )
    }
    const handleBlur = () => {
        setStopBodyScroll(false)
    }
    const closeAutoSuggest = () => {
        searchBarRef.current.blur()
    }

    const showAlcoholBanner = () => {
        return (
            alcoholUtility.isAlcoholRestrictedForArea() || alcoholUtility.isAlcoholRestrictedTime()
        )
    }
    const renderSuggestion = suggestion => {
        const isAlcoholRestrictedHours = showAlcoholBanner() && suggestion.age_restricted
        const currentTab = getTabFromContext()
        // Use catering address for catering tab, shop address for shop tab
        const relevantAddress = currentTab === 'catering' ? cateringAddress : selectedAddress

        return (
            <Link
                to={`/search-results/${util.cleanParamsURL(encodeURI(suggestion.name))}?tab=${currentTab}`}
                state={{
                    product: suggestion,
                    isFromSearch: true,
                    setSearchTermTo: searchTerm || suggestion.category,
                    isAlcoholRestrictedHours: showAlcoholBanner(),
                }}
                className={'searchbar__row'}
                onClick={event => handleRowClick(suggestion, event)}
            >
                {suggestion.thumbnail ? (
                    <img
                        className={
                            isAlcoholRestrictedHours
                                ? 'searchbar__row-grayThumbnail'
                                : 'searchbar__row-thumbnail'
                        }
                        src={suggestion.thumbnail}
                        alt={strings.productImgLabel}
                    />
                ) : (
                    <img
                        className="searchbar__row-thumbnail"
                        src={productFallbackImage}
                        alt={strings.productImgLabel}
                    />
                )}
                <div>
                    {shouldShowPickupOnlyMsg(suggestion) && (
                        <PickupMsg className="pickup-msg-search" />
                    )}
                    <p tabIndex="0" aria-label={suggestion.name}>
                        {suggestion.name}
                    </p>
                    {suggestion.size_value ? (
                        <p
                            className="cartItem__details-pTagSize"
                            tabIndex="0"
                            aria-label={suggestion.size_value}
                        >
                            {strings.sizeLabel} {suggestion.size_value}
                        </p>
                    ) : null}
                    {(suggestion.promo_price || suggestion.item_discount) &&
                    suggestion.quantity > 0 ? (
                        <p>{strings.hereGoesStrikethru}</p>
                    ) : isEmpty(relevantAddress) ? (
                        <p tabIndex="0" className="searchbar__row-approxPrice">
                            {strings.from}
                            {util.formatPrice(suggestion?.catalog_price)}
                        </p>
                    ) : (
                        <p tabIndex="0" aria-label={`${util.formatPrice(suggestion.price)}`}>
                            {util.formatPrice(suggestion.price)}
                        </p>
                    )}
                </div>
                {!isEmpty(relevantAddress) ? (
                    <CustomAddToCartButton
                        cssFlag={'addBtnInsearchBar'}
                        mixpanelFlagForSearch={true}
                        product={suggestion}
                        hidePrice
                        hideAvailabilityContents
                    />
                ) : null}
            </Link>
        )
    }
    const emptySearchDispatch = () => {
        dispatch(resetSearchedProducts())
    }
    const onSuggestionsClearRequested = () => {
        dispatch(resetSearchedProducts())
    }
    const getSuggestionValue = () => {}
    const handleKeyDown = _event => {
        if (_event.key === 'Enter') {
            const currentTab = getTabFromContext()
            const searchQuery = util.searchSuggestRequest(searchTerm, false, currentTab)
            dispatch(setSearchedValue(searchTerm))
            dispatch(loadProducts(searchQuery))
            trackProductSearch(
                'manual',
                searchTerm,
                suggestions.length,
                window.location.pathname,
                store
            )
            searchBarRef.current.blur()
            navigate({
                pathname: '/search-results',
                search: `?query=${encodeURIComponent(searchTerm)}&tab=${currentTab}`,
            })
            setSearchTerm('')
        }
    }
    return (
        <div className={`searchbar ${panelOpen ? 'searchbar-shrink' : '' }`}>
            <img className="searchbar-icon" src={searchIcon} />
            <Autosuggest
                ref={ref => ref && (otherRef.current = ref)}
                renderSuggestionsContainer={renderSuggestionsContainer}
                suggestions={suggestions}
                alwaysRenderSuggestions={searchBarRef.current === document.activeElement}
                getSuggestionValue={getSuggestionValue}
                onSuggestionsFetchRequested={({ value }) => {
                    if (!value) {
                        effectiveDebounce?.cancel && effectiveDebounce.cancel()
                        return
                    }
                    try {
                        const currentTab = getTabFromContext()
                        const searchQuery = util.searchSuggestRequest(value, true, currentTab)
                        effectiveDebounce(() => dispatch(searchProducts(searchQuery)))
                    } catch (e) {
                        emptySearchDispatch()
                    }
                }}
                onSuggestionsClearRequested={onSuggestionsClearRequested}
                focusInputOnSuggestionClick
                inputProps={{
                    className: 'searchbar-enterinput',
                    placeholder: isLoading ? 'Search' : strings.HEADER_SEARCH_PLACEHOLDER,
                    autoComplete: 'off',
                    name: 'product',
                    value: searchTerm || '',
                    onKeyDown: handleKeyDown,
                    onBlur: handleBlur,
                    onChange: (_event, { newValue }) => {
                        let newSearchTerm
                        if (newValue?.length === 0) {
                            newSearchTerm = newValue
                            emptySearchDispatch()
                        } else if (!newValue) newSearchTerm = searchTerm
                        else newSearchTerm = newValue
                        setSearchTerm(newSearchTerm)
                    },
                    onFocus: handleTouch,
                    ref: searchBarRef,
                }}
                renderSuggestion={renderSuggestion}
            />
        </div>
    )
}
export default SearchBar
